import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DIAL_CODES, splitPhone } from "@/components/dashboard/PhoneCard";
import { SECTORS, REGIONS, COUNTRIES, STAGES, SERVICE_TYPES } from "@/lib/constants";
import { Loader2, Lock } from "lucide-react";
import client from "@/api/client";
import { toast } from "sonner";

function Field({ label, children, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Chips({ options, selected, onToggle, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((opt) => {
        const on = (selected || []).includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
              on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function Block({ title, children, testId }) {
  return (
    <div className="rounded-xl border border-border p-3" data-testid={testId}>
      <p className="text-[11px] font-semibold mb-2.5">{title}</p>
      <div className="grid gap-3.5 sm:grid-cols-2">{children}</div>
    </div>
  );
}

/**
 * AdminUserEditForm — one form for everything a member typed in: their
 * account details, their profile fields and their company. Email is
 * read-only by design; changing it would orphan data-room access.
 */
export function AdminUserEditForm({ detail, businessTypes, onSaved }) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const u = detail?.user || {};
  const [name, setName] = useState(u.name || "");
  const [phone, setPhone] = useState(() => splitPhone(u.phone));
  const [btSlug, setBtSlug] = useState(u.business_type?.slug || u.journey?.business_type_slug || "");
  const [profile, setProfile] = useState(detail?.profile || {});
  const [company, setCompany] = useState(detail?.company || null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(u.name || "");
    setPhone(splitPhone(u.phone));
    setBtSlug(u.business_type?.slug || u.journey?.business_type_slug || "");
    setProfile(detail?.profile || {});
    setCompany(detail?.company || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.user?.user_id]);

  const setP = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const toggleP = (k, v) => setProfile((p) => {
    const list = (p[k] || []).includes(v) ? p[k].filter((x) => x !== v) : [...(p[k] || []), v];
    return { ...p, [k]: list };
  });
  const setC = (k, v) => setCompany((c) => ({ ...c, [k]: v }));

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const yrs = parseInt(profile.years_experience, 10);
      await client.patch(`/admin/users/${u.user_id}`, {
        name,
        phone_country: phone.country,
        phone: phone.number,
        business_type_slug: btSlug,
      });
      await client.put(`/admin/users/${u.user_id}/profile`, {
        headline: profile.headline ?? "",
        country: profile.country ?? "",
        bio: profile.bio ?? "",
        region: profile.region ?? "",
        stage: profile.stage ?? "",
        sectors: profile.sectors ?? [],
        expertise: profile.expertise ?? [],
        services: profile.services ?? [],
        investor_focus: profile.investor_focus ?? [],
        service_type: profile.service_type ?? "",
        ticket_size: profile.ticket_size ?? "",
        website: profile.website ?? "",
        linkedin_url: profile.linkedin_url ?? "",
        pitch_doc_url: profile.pitch_doc_url ?? "",
        ...(Number.isFinite(yrs) ? { years_experience: yrs } : {}),
      });
      if (company?.company_id) {
        await client.patch(`/admin/companies/${company.company_id}`, {
          name: company.name || "",
          industry: company.industry || "",
          region: company.region || "",
          stage: company.stage || "",
        });
      }
      toast.success(t("adminUserEdit.saved", { defaultValue: "User details saved" }));
      if (onSaved) await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-3 max-h-[52vh] overflow-y-auto pe-1" data-testid="user-view-edit">
      <Block title={t("adminUserEdit.account", { defaultValue: "Account" })} testId="user-edit-account">
        <Field label={t("usersManager.colUser")}>
          <Input className="rounded-xl h-9" value={name} onChange={(e) => setName(e.target.value)} data-testid="user-edit-name" />
        </Field>
        <Field label={t("usersManager.colEmail")}>
          <div className="flex items-center gap-2 rounded-xl border border-dashed border-border bg-muted/30 px-3 h-9 text-xs text-muted-foreground" data-testid="user-edit-email-readonly">
            <Lock className="h-3 w-3 shrink-0" />
            <span className="truncate" dir="ltr">{u.email}</span>
          </div>
        </Field>
        <Field label={t("usersManager.colPhone")}>
          <div className="flex gap-2">
            <div className="w-[120px]">
              <Select value={phone.country} onValueChange={(v) => setPhone((p) => ({ ...p, country: v }))}>
                <SelectTrigger className="rounded-xl h-9" data-testid="user-edit-phone-country"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {DIAL_CODES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input
              className="rounded-xl h-9 flex-1 font-mono"
              dir="ltr"
              value={phone.number}
              onChange={(e) => setPhone((p) => ({ ...p, number: e.target.value.replace(/[^0-9\s-]/g, "") }))}
              data-testid="user-edit-phone"
            />
          </div>
        </Field>
        <Field label={t("usersManager.colBusinessType", { defaultValue: "Business type" })}>
          <Select value={btSlug} onValueChange={setBtSlug}>
            <SelectTrigger className="rounded-xl h-9" data-testid="user-edit-business-type">
              <SelectValue placeholder={t("companyType.businessTypePlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {(businessTypes || []).map((bt) => (
                <SelectItem key={bt.slug} value={bt.slug}>{(isAr && bt.name_ar) || bt.name_en || bt.slug}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </Block>

      <Block title={t("adminUserView.tabProfile", { defaultValue: "Profile details" })} testId="user-edit-profile">
        <Field label={t("onboarding.headline")} wide>
          <Input className="rounded-xl h-9" value={profile.headline || ""} onChange={(e) => setP("headline", e.target.value)} data-testid="user-edit-headline" />
        </Field>
        <Field label={t("onboarding.bio")} wide>
          <Textarea className="rounded-xl min-h-[80px]" value={profile.bio || ""} onChange={(e) => setP("bio", e.target.value)} data-testid="user-edit-bio" />
        </Field>
        <Field label={t("profileFields.country", { defaultValue: "Country" })}>
          <Select value={profile.country || ""} onValueChange={(v) => setP("country", v)}>
            <SelectTrigger className="rounded-xl h-9" data-testid="user-edit-country"><SelectValue placeholder={t("profileFields.countryPlaceholder", { defaultValue: "Select your country" })} /></SelectTrigger>
            <SelectContent className="max-h-72">{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={t("onboarding.stage")}>
          <Select value={profile.stage || ""} onValueChange={(v) => setP("stage", v)}>
            <SelectTrigger className="rounded-xl h-9" data-testid="user-edit-stage"><SelectValue placeholder={t("onboarding.stage")} /></SelectTrigger>
            <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={t("onboarding.sectors")} wide>
          <Chips options={SECTORS} selected={profile.sectors} onToggle={(v) => toggleP("sectors", v)} testId="user-edit-sectors" />
        </Field>
        <Field label={t("onboarding.expertise")} wide>
          <Chips options={SECTORS} selected={profile.expertise} onToggle={(v) => toggleP("expertise", v)} testId="user-edit-expertise" />
        </Field>
        <Field label={t("onboarding.yearsExperience")}>
          <Input type="number" className="rounded-xl h-9" value={profile.years_experience ?? ""} onChange={(e) => setP("years_experience", e.target.value)} data-testid="user-edit-years" />
        </Field>
        <Field label={t("onboarding.serviceType")}>
          <Select value={profile.service_type || ""} onValueChange={(v) => setP("service_type", v)}>
            <SelectTrigger className="rounded-xl h-9" data-testid="user-edit-service-type"><SelectValue placeholder={t("onboarding.serviceType")} /></SelectTrigger>
            <SelectContent>{SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={t("onboarding.ticketSize")}>
          <Input className="rounded-xl h-9" value={profile.ticket_size || ""} onChange={(e) => setP("ticket_size", e.target.value)} data-testid="user-edit-ticket" />
        </Field>
        <Field label={t("onboarding.website")}>
          <Input className="rounded-xl h-9" value={profile.website || ""} onChange={(e) => setP("website", e.target.value)} data-testid="user-edit-website" />
        </Field>
        <Field label={t("onboarding.linkedinUrl")}>
          <Input className="rounded-xl h-9" value={profile.linkedin_url || ""} onChange={(e) => setP("linkedin_url", e.target.value)} data-testid="user-edit-linkedin" />
        </Field>
        <Field label={t("onboarding.pitchDocUrl")}>
          <Input className="rounded-xl h-9" value={profile.pitch_doc_url || ""} onChange={(e) => setP("pitch_doc_url", e.target.value)} data-testid="user-edit-pitch" />
        </Field>
      </Block>

      {company?.company_id && (
        <Block title={t("adminUserView.business", { defaultValue: "Business" })} testId="user-edit-company">
          <Field label={t("dashboard.companyTitle")}>
            <Input className="rounded-xl h-9" value={company.name || ""} onChange={(e) => setC("name", e.target.value)} data-testid="user-edit-company-name" />
          </Field>
          <Field label={t("companyType.industry")}>
            <Select value={company.industry || ""} onValueChange={(v) => setC("industry", v)}>
              <SelectTrigger className="rounded-xl h-9" data-testid="user-edit-company-industry"><SelectValue placeholder={t("companyType.industryPlaceholder")} /></SelectTrigger>
              <SelectContent>{SECTORS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("onboarding.region")}>
            <Select value={company.region || ""} onValueChange={(v) => setC("region", v)}>
              <SelectTrigger className="rounded-xl h-9" data-testid="user-edit-company-region"><SelectValue placeholder={t("onboarding.region")} /></SelectTrigger>
              <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("onboarding.stage")}>
            <Select value={company.stage || ""} onValueChange={(v) => setC("stage", v)}>
              <SelectTrigger className="rounded-xl h-9" data-testid="user-edit-company-stage"><SelectValue placeholder={t("onboarding.stage")} /></SelectTrigger>
              <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </Block>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <Button type="submit" size="sm" className="rounded-xl gap-2" disabled={saving} data-testid="user-edit-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
}

export default AdminUserEditForm;
